/**
 * MonographPanelPrimitives - token-bound panel primitives for companion demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { CiteLinks } from "./CiteLinks.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import { cn } from "../styling/index.js";
import { createMonographPanelComposites } from "./MonographPanelComposites.js";
import { createMonographPanelControls } from "./MonographPanelControls.js";
import { createMonographPanelFoundations } from "./MonographPanelFoundations.js";
import { createMonographPanelLists } from "./MonographPanelLists.js";
import { createMonographPanelNarrative } from "./MonographPanelNarrative.js";
import { createMonographPanelProductGrid } from "./MonographPanelProductGrid.js";
import { createMonographPanelSignals } from "./MonographPanelSignals.js";
import { createMonographPanelTables } from "./MonographPanelTables.js";
import { createMonographPanelThesis } from "./MonographPanelThesis.js";
import { createMonographPanelTypography } from "./MonographPanelTypography.js";
import { Switch } from "./primitives/Switch.js";
import type {
  MonographMonoProps,
  MonographMicroLabelProps,
  MonographPanelCardProps,
  MonographPanelPrimitiveOptions,
  MonographPanelPrimitives,
  MonographPanelShellProps,
  MonographRepositoryLinksProps,
  MonographPanelTokens,
  MonographSectionHeadingProps,
  MonographToggleProps,
} from "./MonographPanelPrimitiveTypes.js";

export type * from "./MonographPanelPrimitiveTypes.js";

function pointerSpotPosition(event: React.MouseEvent<HTMLDivElement>): readonly [string, string] {
  const rect = event.currentTarget.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  return [`${x.toFixed(1)}%`, `${y.toFixed(1)}%`];
}

export function createMonographPanelPrimitives(
  tokens: MonographPanelTokens,
  options: MonographPanelPrimitiveOptions = {},
): MonographPanelPrimitives {
  const panelOptions = options.panel ?? {};
  const cardOptions = options.card ?? {};
  const monoOptions = options.mono ?? {};
  const badgeOptions = options.badge ?? {};
  const kpiOptions = options.kpi ?? {};
  const chipOptions = options.chip ?? {};
  const sectionOptions = options.sectionHeading ?? {};
  const verdictOptions = options.verdict ?? {};
  const composites = createMonographPanelComposites({ tokens });
  const controls = createMonographPanelControls({ tokens });
  const foundations = createMonographPanelFoundations({
    chipOptions,
    kpiOptions,
    tokens,
    verdictOptions,
  });
  const lists = createMonographPanelLists({ tokens });
  const narrative = createMonographPanelNarrative({ tokens });
  const signals = createMonographPanelSignals({ tokens });
  const tables = createMonographPanelTables({ tokens });
  const typography = createMonographPanelTypography({ tokens });
  const thesis = createMonographPanelThesis({
    Card,
    Prose: typography.Prose,
    PullQuote: signals.PullQuote,
    SectionHeading,
    tokens,
  });
  const productGrid = createMonographPanelProductGrid({
    DataTable: tables.DataTable,
    tokens,
  });

  function PanelShell({
    title,
    subtitle,
    accentColor,
    children,
    badges,
    eyebrow,
    intro,
    chapter,
  }: MonographPanelShellProps): React.JSX.Element {
    const eyebrowContent = eyebrow ?? chapter;
    return (
      <div
        data-csk-panel={panelOptions.dataAttribute}
        key={title}
        style={{
          animation: panelOptions.enterAnimation,
          margin: "0 auto",
          maxWidth: panelOptions.maxWidth ?? 1100,
          minHeight: "100vh",
          padding: panelOptions.padding ?? "32px 32px 64px",
        }}
      >
        <div style={{ marginBottom: 28 }}>
          {eyebrowContent ? (
            <div
              style={{
                color: accentColor,
                fontSize: panelOptions.eyebrowFontSize ?? 11,
                letterSpacing: 0,
                marginBottom: panelOptions.eyebrowMarginBottom ?? 8,
                opacity: panelOptions.eyebrowOpacity ?? 0.85,
                textTransform: "uppercase",
              }}
            >
              {eyebrowContent}
            </div>
          ) : null}
          <h1
            style={{
              background: panelOptions.gradientTitle
                ? `linear-gradient(135deg, ${tokens.bright} 0%, ${accentColor} 140%)`
                : undefined,
              backgroundClip: panelOptions.gradientTitle ? "text" : undefined,
              color: tokens.bright,
              fontSize: panelOptions.titleFontSize ?? 26,
              fontWeight: 800,
              letterSpacing: panelOptions.titleLetterSpacing ?? 0,
              lineHeight: panelOptions.titleLineHeight,
              margin: 0,
              WebkitBackgroundClip: panelOptions.gradientTitle ? "text" : undefined,
              WebkitTextFillColor: panelOptions.gradientTitle ? "transparent" : undefined,
            }}
          >
            {title}
          </h1>
          <p
            style={{
              color: tokens.muted,
              fontSize: 13,
              letterSpacing: 0,
              margin: panelOptions.subtitleMargin ?? "6px 0 0",
            }}
          >
            {subtitle}
          </p>
          {badges?.length ? (
            <div style={{ marginTop: 12 }}>
              <composites.BadgeRail items={badges} />
            </div>
          ) : null}
          {intro ? (
            <div
              style={{
                color: tokens.text,
                fontSize: 14,
                lineHeight: 1.6,
                marginTop: 14,
                maxWidth: "68ch",
              }}
            >
              {intro}
            </div>
          ) : null}
          <div
            style={{
              background: `linear-gradient(90deg, ${accentColor}, transparent)`,
              borderRadius: 1,
              boxShadow: panelOptions.dividerShadow
                ? `0 0 12px ${colorWithAlpha(accentColor, "55")}`
                : undefined,
              height: 2,
              marginTop: 14,
              maxWidth: panelOptions.dividerMaxWidth ?? 220,
              opacity: panelOptions.dividerOpacity ?? 0.7,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: panelOptions.contentGap ?? 18,
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  function Card({
    children,
    style,
    glowColor,
    className,
    compact,
  }: MonographPanelCardProps): React.JSX.Element {
    return (
      <div
        className={cn(
          cardOptions.trackPointerSpot ? "csk-monograph-spot-card" : undefined,
          cardOptions.className,
          className,
        )}
        onMouseMove={
          cardOptions.trackPointerSpot
            ? (event): void => {
                const [x, y] = pointerSpotPosition(event);
                event.currentTarget.style.setProperty("--csk-monograph-spot-x", x);
                event.currentTarget.style.setProperty("--csk-monograph-spot-y", y);
              }
            : undefined
        }
        style={{
          backdropFilter: cardOptions.backdropFilter ?? "blur(12px)",
          background: cardOptions.background ?? "rgba(7,6,13,0.7)",
          border: `1px solid ${tokens.border}`,
          borderRadius: cardOptions.borderRadius ?? 16,
          boxShadow: glowColor
            ? (cardOptions.glowBoxShadow?.(glowColor) ??
              `0 6px 28px rgba(0,0,0,0.4), 0 0 0 1px ${glowColor}`)
            : (cardOptions.boxShadow ?? "0 6px 28px rgba(0,0,0,0.35)"),
          overflow: cardOptions.overflow,
          padding: compact ? "14px 16px" : (cardOptions.padding ?? "18px 20px"),
          position: cardOptions.trackPointerSpot ? "relative" : undefined,
          ...style,
        }}
      >
        {children}
      </div>
    );
  }

  function ListCard({ style, ...props }: MonographPanelCardProps): React.JSX.Element {
    return <Card {...props} style={{ padding: 0, overflow: "hidden", ...style }} />;
  }

  function TableCard({ style, ...props }: MonographPanelCardProps): React.JSX.Element {
    return <Card {...props} style={{ padding: 0, overflowX: "auto", ...style }} />;
  }

  function SectionHeading({ children, style }: MonographSectionHeadingProps): React.JSX.Element {
    return (
      <h2
        style={{
          color: tokens.muted,
          fontSize: sectionOptions.fontSize ?? 11,
          fontWeight: 700,
          letterSpacing: sectionOptions.letterSpacing ?? 0,
          margin: sectionOptions.margin ?? "8px 0 -4px",
          textTransform: "uppercase",
          ...style,
        }}
      >
        {children}
      </h2>
    );
  }

  function MicroLabel({
    children,
    color,
    className,
    style,
  }: MonographMicroLabelProps): React.JSX.Element {
    return (
      <div
        className={className}
        style={{
          color: color ?? tokens.muted,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          lineHeight: 1.4,
          textTransform: "uppercase",
          ...style,
        }}
      >
        {children}
      </div>
    );
  }

  function Mono({ children, style }: MonographMonoProps): React.JSX.Element {
    return (
      <code
        style={{
          background: monoOptions.background ?? "rgba(7,6,13,0.7)",
          border: `1px solid ${tokens.border}`,
          borderRadius: monoOptions.borderRadius ?? 10,
          color: tokens.text,
          display: "block",
          fontFamily: tokens.mono,
          fontSize: monoOptions.fontSize ?? 12.5,
          lineHeight: monoOptions.lineHeight ?? 1.55,
          overflowX: "auto",
          padding: monoOptions.padding ?? "10px 14px",
          whiteSpace: "pre-wrap",
          wordBreak: monoOptions.wordBreak ?? "break-word",
          ...style,
        }}
      >
        {children}
      </code>
    );
  }

  function badge(label: string, color: string, title?: string): React.JSX.Element {
    return (
      <span
        key={label}
        title={title}
        style={{
          background: colorWithAlpha(color, badgeOptions.backgroundAlpha ?? "18"),
          border: `1px solid ${colorWithAlpha(color, badgeOptions.borderAlpha ?? "55")}`,
          borderRadius: 999,
          color,
          display: "inline-block",
          fontFamily: tokens.mono,
          fontSize: badgeOptions.fontSize ?? 10,
          fontWeight: 600,
          letterSpacing: badgeOptions.letterSpacing ?? 0,
          padding: "2px 8px",
        }}
      >
        {label}
      </span>
    );
  }

  function RepositoryLinks({
    items,
    heading = "Open in the repository",
    label,
    ariaLabel,
    tone,
    glowColor,
    cardStyle,
    className,
  }: MonographRepositoryLinksProps): React.JSX.Element {
    return (
      <>
        <SectionHeading>{heading}</SectionHeading>
        <Card
          {...(glowColor === undefined ? {} : { glowColor })}
          {...(cardStyle === undefined ? {} : { style: cardStyle })}
          {...(className === undefined ? {} : { className })}
        >
          <CiteLinks
            items={items}
            tone={tone ?? tokens.accent}
            theme={tokens}
            {...(label === undefined ? {} : { label })}
            {...(ariaLabel === undefined ? {} : { ariaLabel })}
          />
        </Card>
      </>
    );
  }

  function Toggle({
    id,
    label,
    checked,
    onChange,
    color,
    accent,
    description,
  }: MonographToggleProps): React.JSX.Element {
    const tone = color ?? accent ?? tokens.accent;
    const labelId = React.useId();
    const descId = React.useId();
    return (
      <div
        role="group"
        onClick={(e) => {
          // Avoid double-toggle when clicking the switch itself.
          if ((e.target as HTMLElement).closest("[role=switch]")) return;
          onChange(!checked);
        }}
        style={{
          alignItems: "flex-start",
          background: checked ? colorWithAlpha(tone, "15") : "rgba(7,6,13,0.45)",
          border: checked
            ? `1px solid ${colorWithAlpha(tone, "88")}`
            : `1px solid ${tokens.border}`,
          borderRadius: 12,
          boxShadow: checked ? `0 0 14px ${colorWithAlpha(tone, "26")}` : "none",
          cursor: "pointer",
          display: "flex",
          gap: 10,
          padding: "10px 12px",
          textAlign: "left",
          transition: "all 150ms ease",
          width: "100%",
        }}
      >
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={onChange}
          aria-labelledby={labelId}
          aria-describedby={description ? descId : undefined}
          style={{
            background: checked ? tone : tokens.border,
            borderRadius: 999,
            flexShrink: 0,
            height: 18,
            marginTop: 2,
            position: "relative",
            transition: "background 150ms ease",
            width: 32,
          }}
        />
        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span
            id={labelId}
            style={{ color: checked ? tone : tokens.bright, fontSize: 13, fontWeight: 700 }}
          >
            {label}
          </span>
          {description ? (
            <span
              id={descId}
              style={{
                color: tokens.muted,
                fontSize: 11.5,
                lineHeight: 1.5,
                marginTop: 2,
              }}
            >
              {description}
            </span>
          ) : null}
        </span>
      </div>
    );
  }

  return {
    PanelShell,
    Card,
    ListCard,
    TableCard,
    SectionHeading,
    MicroLabel,
    Mono,
    InlineCode: narrative.InlineCode,
    Prose: typography.Prose,
    KpiBox: foundations.KpiBox,
    KpiGrid: composites.KpiGrid,
    PanelGrid: composites.PanelGrid,
    DataTable: tables.DataTable,
    NeutralizationModeProductGrid: productGrid.NeutralizationModeProductGrid,
    Badge: foundations.Badge,
    BadgeRail: composites.BadgeRail,
    FactList: composites.FactList,
    FeatureGrid: narrative.FeatureGrid,
    ContentList: lists.ContentList,
    StepList: narrative.StepList,
    MeterBar: signals.MeterBar,
    PullQuote: signals.PullQuote,
    ThesisCard: thesis.ThesisCard,
    ChipRow: foundations.ChipRow,
    ChipMultiSelect: controls.ChipMultiSelect,
    Toggle,
    VerdictBanner: foundations.VerdictBanner,
    Callout: composites.Callout,
    StatusList: composites.StatusList,
    RepositoryLinks,
    badge,
  };
}
